// Phase 23A — Admin/Manager PJP Calendar + Cycle editor
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, ChevronRight, CalendarDays, Upload, Download, RefreshCw,
  Repeat, X, Check,
} from "lucide-react";
import { api, API_BASE, getToken, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { SearchableSelect } from "@/components/ui/searchable-select";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const SOURCE_META = {
  admin:            { letter: "M", color: "#6D2C91", label: "Manual (admin)" },
  cycle:            { letter: "C", color: "#0ea5e9", label: "Cycle" },
  approved_request: { letter: "R", color: "#059669", label: "Approved request" },
  bulk_import:      { letter: "B", color: "#f59e0b", label: "Bulk import" },
  weekday_fallback: { letter: "W", color: "#a3a3a3", label: "Weekday fallback" },
};

function monthLabel(iso) {
  return new Date(iso + "-01").toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function daysGrid(month) {
  const [y, m] = month.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const firstWeekday = first.getDay();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  const daysInMonth = new Date(y, m, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  }
  return cells;
}

export default function PJPCalendar() {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState({}); // {date: beat_id|null}
  const [saving, setSaving] = useState(false);
  const [beats, setBeats] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [cycleOpen, setCycleOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    // PJP applies to field/office roles, never DISTRIBUTOR — filter
    // server-side via role_code instead of fetching one page of "latest
    // created" users and filtering client-side. With 267+ distributor
    // logins now in the system (all newer than most other users), that
    // client-side filter zeroed out page 1 entirely since it was 100%
    // distributor rows.
    api.get("/users", {
      params: { page_size: 500, role_code: "SUPER_ADMIN,MANAGEMENT,SALES_HEAD,MIS,ACCOUNTS,ZSM,RSM,ASM,SE" },
    })
      .then(r => setUsers(r.data.items || []))
      .catch(e => toast.error(formatApiError(e)));
  }, []);

  // Beat picker is scoped to the selected SE: only beats assigned to them,
  // or carried by a distributor mapped to them — not every beat in the system.
  useEffect(() => {
    if (!userId) { setBeats([]); return; }
    let cancelled = false;
    api.get("/beats", { params: { page_size: 2000, for_user_id: userId } })
      .then(r => { if (!cancelled) setBeats(r.data.items || []); })
      .catch(e => { if (!cancelled) toast.error(formatApiError(e)); });
    return () => { cancelled = true; };
  }, [userId]);

  const load = useCallback(async () => {
    if (!userId || !month) return;
    setLoading(true);
    try {
      const r = await api.get("/pjp/calendar", { params: { user_id: userId, month } });
      setData(r.data);
      setPending({});
    } catch (e) { toast.error(formatApiError(e)); setData(null); }
    finally { setLoading(false); }
  }, [userId, month]);
  useEffect(() => { load(); }, [load]);

  const cells = useMemo(() => daysGrid(month), [month]);
  const dayMap = useMemo(() => {
    const m = {};
    (data?.days || []).forEach(d => { m[d.date] = d; });
    return m;
  }, [data]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const stagePending = (date, beat_id) => {
    setPending(p => ({ ...p, [date]: beat_id }));
  };

  const savePending = async () => {
    const items = Object.entries(pending).map(([date, beat_id]) => ({ date, beat_id }));
    if (!items.length) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const r = await api.post("/pjp/assign", { user_id: userId, assignments: items });
      toast.success(`Saved · ${r.data.written} written, ${r.data.cleared} cleared`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Immediate removal of a saved beat on one day (no separate Save step).
  const removeDay = async (iso) => {
    const beat = beats.find(b => b.id === dayMap[iso]?.beat_id);
    if (!window.confirm(`Remove ${beat ? `"${beat.name}"` : "the beat"} from ${iso}? This takes effect immediately.`)) return;
    setSaving(true);
    try {
      await api.post("/pjp/assign", { user_id: userId, assignments: [{ date: iso, beat_id: null }] });
      toast.success(`Beat removed from ${iso}`);
      setPending(p => { const n = { ...p }; delete n[iso]; return n; });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const doExport = async () => {
    try {
      const r = await fetch(`${API_BASE}/pjp/export?month=${month}` + (userId ? `&user_id=${userId}` : ""),
        { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const b = await r.blob();
      const u = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = `pjp_effective_${month}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
      toast.success("Exported");
    } catch (e) { toast.error(String(e.message || e)); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="pjp-calendar-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Field ops" }, { label: "PJP calendar" }]}
        title="PJP Admin Calendar"
        subtitle="Journey plan calendar view across users and beats."
        favorite={{ key: "admin.pjp", type: "screen", label: "PJP Admin Calendar", route: "/admin/pjp" }}
        testid="pjpcalendar-page-header"
      />

      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-[#6D2C91]" /> PJP Calendar
          </h1>
          <p className="text-xs text-neutral-500 mt-0.5">
            Assign beats per day · cycles repeat automatically · bulk import + effective export.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="pjp-import-btn">
            <Upload className="h-4 w-4 mr-1" /> Import
          </Button>
          <Button variant="outline" size="sm" onClick={doExport} data-testid="pjp-export-btn">
            <Download className="h-4 w-4 mr-1" /> Export
          </Button>
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3 flex flex-wrap items-end gap-3">
          <div className="min-w-[260px]">
            <Label className="text-[11px] text-neutral-500">User</Label>
            <SearchableSelect value={userId} onChange={setUserId}
              options={users.map(u => ({ id: u.id, code: u.role_code, name: `${u.name} · ${u.role_code}` }))}
              placeholder="Select user…" testid="pjp-user" />
          </div>
          <div className="flex items-end gap-1">
            <Button variant="outline" size="icon" onClick={() => shiftMonth(-1)} data-testid="pjp-prev-month">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="px-3 py-1.5 rounded-md bg-neutral-100 text-sm font-semibold min-w-[140px] text-center" data-testid="pjp-month-label">
              {monthLabel(month)}
            </div>
            <Button variant="outline" size="icon" onClick={() => shiftMonth(1)} data-testid="pjp-next-month">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={() => setCycleOpen(true)} disabled={!userId}
                  data-testid="pjp-cycle-btn">
            <Repeat className="h-4 w-4 mr-1" /> Define / edit cycle
          </Button>
          {Object.keys(pending).length > 0 && (
            <Button onClick={savePending} disabled={saving}
                    className="bg-[#6D2C91] text-white hover:bg-[#5b2478]"
                    data-testid="pjp-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              Save {Object.keys(pending).length} change(s)
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-[11px] text-neutral-600" data-testid="pjp-legend">
        {Object.entries(SOURCE_META).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded text-white text-[10px] font-bold"
                  style={{ background: v.color }}>{v.letter}</span>
            {v.label}
          </span>
        ))}
      </div>

      {/* Calendar grid */}
      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3">
          {loading && <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></div>}
          {!loading && !userId && (
            <div className="py-16 text-center text-neutral-400 text-sm">Select a user to view/edit their PJP.</div>
          )}
          {!loading && userId && (
            <div className="grid grid-cols-7 gap-1" data-testid="pjp-grid">
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
                <div key={d} className="text-[10px] uppercase text-neutral-500 font-semibold text-center py-1">{d}</div>
              ))}
              {cells.map((iso, idx) => {
                if (!iso) return <div key={idx} className="h-20 bg-neutral-50 rounded" />;
                const day = dayMap[iso];
                const staged = pending[iso];
                const effectiveBeatId = staged !== undefined ? staged : (day?.beat_id || null);
                const beat = beats.find(b => b.id === effectiveBeatId);
                const src = staged !== undefined ? "admin" : (day?.source || null);
                const isStaged = staged !== undefined;
                const dayNum = parseInt(iso.split("-")[2]);
                const srcMeta = src ? SOURCE_META[src] : null;
                return (
                  <button
                    key={iso}
                    onClick={() => setSelectedDate(iso)}
                    className={`h-20 rounded border p-1 text-left flex flex-col ${
                      selectedDate === iso ? "border-[#6D2C91] ring-2 ring-[#6D2C91]/30" : "border-neutral-200 hover:border-neutral-400"
                    } ${isStaged ? "bg-amber-50" : "bg-white"}`}
                    data-testid={`pjp-day-${iso}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-neutral-700">{dayNum}</span>
                      {srcMeta && (
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded text-white text-[10px] font-bold"
                              style={{ background: srcMeta.color }}
                              title={srcMeta.label}
                              data-testid={`pjp-src-${iso}`}>
                          {srcMeta.letter}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-neutral-600 mt-auto truncate" title={beat?.name}>
                      {beat ? beat.name : (effectiveBeatId ? "…" : "")}
                    </div>
                    {isStaged && <div className="text-[9px] text-amber-700">pending</div>}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Day side panel */}
      {selectedDate && userId && (
        <DayPanel date={selectedDate} beats={beats}
                    currentBeatId={pending[selectedDate] !== undefined ? pending[selectedDate] : dayMap[selectedDate]?.beat_id}
                    onAssign={(b) => stagePending(selectedDate, b)}
                    pendingCount={Object.keys(pending).length} onSave={savePending} saving={saving}
                    daySource={dayMap[selectedDate]?.source || null} hasSaved={!!dayMap[selectedDate]?.beat_id}
                    onRemove={() => removeDay(selectedDate)}
                    onClose={() => setSelectedDate(null)} />
      )}

      {/* Cycle modal */}
      <CycleModal open={cycleOpen} onOpenChange={setCycleOpen}
                   userId={userId} beats={beats} activeCycle={data?.active_cycle}
                   onSaved={load} />

      {/* Import modal */}
      <ImportModal open={importOpen} onOpenChange={setImportOpen} onImported={load} />
    </div>
  );
}

function DayPanel({ date, beats, currentBeatId, onAssign, onClose, pendingCount = 0, onSave, saving, daySource, hasSaved, onRemove }) {
  const [beatSearch, setBeatSearch] = useState("");
  const filteredBeats = useMemo(() => {
    const q = beatSearch.trim().toLowerCase();
    if (!q) return beats;
    return beats.filter(b =>
      (b.code || "").toLowerCase().includes(q) || (b.name || "").toLowerCase().includes(q));
  }, [beats, beatSearch]);
  return (
    <Card className="border-[#6D2C91]/30 rounded-2xl" data-testid={`pjp-day-panel-${date}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-[#6D2C91]">{date}</div>
            <div className="text-xs text-neutral-500">Pick a beat, or Clear to fall back to cycle. Then press <b>Save</b> — nothing changes until you save.</div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <Input placeholder="Search beat code or name…" value={beatSearch}
               onChange={(e) => setBeatSearch(e.target.value)}
               className="h-8 text-sm" data-testid="pjp-day-beat-search" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-52 overflow-auto">
          {filteredBeats.map(b => (
            <button key={b.id} onClick={() => onAssign(b.id)}
                     className={`text-left border rounded-lg px-2 py-1.5 text-xs ${
                       currentBeatId === b.id
                         ? "border-[#6D2C91] bg-[#6D2C91]/5"
                         : "border-neutral-200 hover:border-[#6D2C91]/40"
                     }`}
                     data-testid={`pjp-beat-pick-${b.id}`}>
              <div className="font-semibold">{b.code}</div>
              <div className="text-neutral-500 truncate">{b.name}</div>
            </button>
          ))}
          {filteredBeats.length === 0 && (
            <div className="col-span-full text-center text-xs text-neutral-400 py-3">
              No beats match "{beatSearch}".
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 flex-wrap items-center">
          {hasSaved && daySource === "cycle" && (
            <span className="text-[11px] text-neutral-500 mr-auto">This beat comes from the repeating cycle — pick a different beat for this day, or edit the cycle.</span>
          )}
          {hasSaved && daySource !== "cycle" && (
            <Button variant="outline" size="sm" onClick={onRemove} disabled={saving}
                    className="mr-auto border-red-200 text-red-600 hover:bg-red-50" data-testid="pjp-day-remove">
              Remove beat from this day
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onAssign(null)} data-testid="pjp-day-clear">
            Clear
          </Button>
          {pendingCount > 0 && (
            <Button size="sm" onClick={onSave} disabled={saving} className="bg-[#6D2C91] text-white hover:bg-[#5b2478]"
                    data-testid="pjp-day-save">
              <Check className="h-4 w-4 mr-1" /> Save {pendingCount} change(s)
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CycleModal({ open, onOpenChange, userId, beats, activeCycle, onSaved }) {
  const [startDate, setStartDate] = useState("");
  const [freq, setFreq] = useState(7);
  const [pattern, setPattern] = useState(Array(7).fill(null));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (activeCycle) {
        setStartDate(activeCycle.start_date);
        setPattern(activeCycle.pattern_days || Array(7).fill(null));
        setFreq((activeCycle.pattern_days || []).length || 7);
      } else {
        setStartDate(new Date().toISOString().slice(0, 10));
        setFreq(7);
        setPattern(Array(7).fill(null));
      }
    }
  }, [open, activeCycle]);

  const changeFreq = (n) => {
    n = Math.max(1, Math.min(30, parseInt(n) || 1));
    setFreq(n);
    setPattern(p => {
      const arr = p.slice(0, n);
      while (arr.length < n) arr.push(null);
      return arr;
    });
  };

  const save = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      await api.post("/pjp/cycles", { user_id: userId, start_date: startDate, pattern_days: pattern });
      toast.success("Cycle saved");
      onOpenChange(false);
      await onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pjp-cycle-modal" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Define cycle</DialogTitle>
          <DialogDescription>Pattern repeats from start date. Off-days: leave beat unset.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-neutral-500">Start date</Label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                    data-testid="pjp-cycle-start" />
          </div>
          <div>
            <Label className="text-xs text-neutral-500">Frequency (days)</Label>
            <Input type="number" min={1} max={30} value={freq} onChange={e => changeFreq(e.target.value)}
                    data-testid="pjp-cycle-freq" />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-auto" data-testid="pjp-cycle-slots">
          {pattern.map((beat_id, i) => (
            <div key={i} className="border border-neutral-200 rounded-lg p-2">
              <div className="text-[10px] uppercase text-neutral-500">Day {i + 1}</div>
              <select value={beat_id || ""} onChange={e => setPattern(p => { const q=[...p]; q[i] = e.target.value || null; return q; })}
                       className="w-full text-xs border rounded px-1 py-1 mt-1"
                       data-testid={`pjp-cycle-slot-${i}`}>
                <option value="">— Off —</option>
                {beats.map(b => <option key={b.id} value={b.id}>{b.code} · {b.name}</option>)}
              </select>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#6D2C91] text-white hover:bg-[#5b2478]"
                  data-testid="pjp-cycle-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Save cycle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportModal({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doImport = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API_BASE}/pjp/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error?.message || j.detail?.message || j.detail || `HTTP ${r.status}`);
      setResult(j);
      toast.success(`Imported · ${j.written} written · ${j.row_errors.length} errors`);
      await onImported();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pjp-import-modal">
        <DialogHeader>
          <DialogTitle>Bulk PJP import</DialogTitle>
          <DialogDescription>CSV or XLSX with columns: USER_CODE, DATE (yyyy-mm-dd), BEAT_CODE (empty = clear).</DialogDescription>
        </DialogHeader>
        <Input type="file" accept=".xlsx,.csv,.xlsm" onChange={e => setFile(e.target.files?.[0])} data-testid="pjp-import-file" />
        {result && (
          <div className="text-xs space-y-1">
            <div><b>Written:</b> {result.written} · <b>Cleared:</b> {result.cleared} · <b>Errors:</b> {result.row_errors.length}</div>
            {result.row_errors.slice(0, 5).map(e => (
              <div key={e.row} className="text-rose-600">Row {e.row}: {e.error}</div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={doImport} disabled={!file || busy}
                  className="bg-[#6D2C91] text-white hover:bg-[#5b2478]"
                  data-testid="pjp-import-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
